import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../core/event-bus';
import { IData, IDataSettings, ITimes, ICalorieData, IDataServerTime, IOnlineOrderingSettings, ILocationSettings, IStoreLocation, IDataRedeemItemDisplayGroup, IDataLoyaltyItem, DataLoyalty, IItemOrderTypeSize } from './data.interface';
import { Util } from '../utils/util';
import { LoaderSpinner } from '../components/shared/LoaderSpinner/loader-spinner';
import { Context } from './context';
import { ILoyaltyProvider } from '@ts/services/loyalty-provider/loyalty-provider.interface';
import { AutocompleteProvider, BirthdayRequirement, EmailTextMarketingType, LoyaltyProvider, MapProvider, MealDealLocationUiOption, OrderType, Pages, RoundingRule } from '../types/enums';
import { GeoServiceProviderName } from '../types/geo-service-provider-name';
import { HttpService } from '../services/http-service';
import { IRegionalizedBanner, IBannerImage, IBannerSchedule, IBannerRegion, IRegionZonePoint } from '../services/banner-service.interface';
import { LoyaltyInfoRaw } from './context.interface';

export class Data {
    private readonly _eventBus: EventEmitter;
    private readonly _http: HttpService;
    private readonly _context: Context;
    private readonly _apiOnlineOrderingUrl: string;
    private readonly _useDataV4: boolean;
    private _brandData: IData | null = null;
    private readonly _datas: Record<string, IData> = {};
    private readonly _timeOffsetCache: Record<string, number> = {};
    private readonly _brandKey: string;
    private readonly _calories: Record<number, ICalorieData[]> = {};
    private readonly _contentFolderUrl: string;
    private _selectedStoreKey: string = "";
    private readonly _locations: IStoreLocation[];
    private readonly _loyaltyInfo: DataLoyalty;

    constructor(eventBus: EventEmitter, http: HttpService, context: Context, apiOnlineOrderingUrl: string, locations: any[], loyaltyInfo: LoyaltyInfoRaw) {
        this._eventBus = eventBus;
        this._http = http;
        this._context = context;
        this._apiOnlineOrderingUrl = apiOnlineOrderingUrl;
        this._contentFolderUrl = context.getProperty("publicStorageUrl");
        this._brandKey = context.getProperty("brandInfo").StoreKey;
        this._useDataV4 = context.getProperty("useDataV4");
        this._locations = this.mapLocationsData(locations);
        this._loyaltyInfo = this._mapLoyaltyData(loyaltyInfo);

        this._eventBusListeners();
    }

    get storeKey(): string {
        return this._selectedStoreKey;
    }

    get isBrand(): boolean {
        return this._brandKey == this._selectedStoreKey;
    }

    get isBrandLoaded(): boolean {
        return !!this._brandData;
    }

    get locations(): IStoreLocation[] {
        return this._locations;
    }

    public clearStoreKey(): void {
        this._selectedStoreKey = "";
    }

    public updateLocationsDistance(userLatitude: number, userLongitude: number): void {
        this._locations.forEach(location => {
            location.distance = Util.GetDistance(userLatitude, userLongitude, location.latitude, location.longitude, 'M');
        });
        this._locations.sort((a: IStoreLocation, b: IStoreLocation) => a.distance - b.distance);
    }

    public async getStoreDataAsync(storeKey: string, loyaltyProvider: ILoyaltyProvider): Promise<IData | null> {
        if (this._datas[storeKey])
            return this._datas[storeKey];

        const spinner = new LoaderSpinner();
        await this._loadDataAsync(storeKey, loyaltyProvider, true);
        spinner.destroy();

        return this._datas[storeKey] || null;
    }

    private readonly _eventBusListeners = (): void => {
        this._eventBus.on(EventTypes.STORE_CHANGED, this._storeChangedAsync);
    }

    public async setStoreAndGetData(storeKey: string, loyaltyProvider: ILoyaltyProvider): Promise<void> {
        await this._storeChangedAsync(storeKey, loyaltyProvider);
    }

    private readonly _storeChangedAsync = async (storeKey: string, loyaltyProvider: ILoyaltyProvider): Promise<void> => {
        if (this._selectedStoreKey === storeKey)
            return;

        this._eventBus.emit(EventTypes.MENU_PLACEHOLDERS);
        this._selectedStoreKey = storeKey;
        await this._loadDataAsync(storeKey ?? "", loyaltyProvider, false);
    }

    public getLoyaltyInfo(): DataLoyalty {
        return this._loyaltyInfo;
    }

    public getProperty<K extends keyof IData>(property: K): IData[K] {
        const storeData = this._datas[this._selectedStoreKey];
        if (!storeData)
            return this._getDataDefaultValue(property);

        return storeData[property];
    }

    public getBrandProperty<K extends keyof IData>(property: K): IData[K] {
        if (!this._brandData)
            return this._getDataDefaultValue(property);

        return this._brandData[property];
    }

    public setProperty<K extends keyof IData>(key: K, value: IData[K]) {
        this._datas[this._selectedStoreKey][key] = value;
    }

    public async loadBrandDataAsync(): Promise<void> {
        const brandUrl = `${this._contentFolderUrl}/online3/online-brand-data-${this._brandKey}.json`;
        const brand = await fetch(brandUrl).then(r => r.json());
        const data = this._mapData(brand, true);
        if (data) {
            this._brandData = data;
            this._brandData.TimeOffset = await this._loadTimeOffsetAsync(this._brandKey);
            this._setDefaultOrderType();
            this._eventBus.emit(EventTypes.BRAND_DATA_RETRIEVED);
        }
    }

    private _setDefaultOrderType(): void {
        const orderTypes = this._brandData?.OrderTypeSubTypes ?? [];
        if (!orderTypes.length)
            return;

        const lowestSortingIndex = Math.min(...orderTypes.map(x => x.SortingIndex ?? 0));
        const tiedForLowest = orderTypes.filter(x => (x.SortingIndex ?? 0) === lowestSortingIndex);

        // Delivery wins ties on sorting index; otherwise the lowest order type ID wins.
        const defaultOrderType = tiedForLowest.some(x => x.OrderTypeId === OrderType.DELIVERY)
            ? OrderType.DELIVERY
            : tiedForLowest.reduce((lowest, x) => x.OrderTypeId < lowest.OrderTypeId ? x : lowest, tiedForLowest[0]).OrderTypeId;

        this._context.setProperty("defaultOrderType", defaultOrderType);
    }

    private readonly _loadDataAsync = async (storeKey: string, loyaltyProvider: ILoyaltyProvider, skipEvent: boolean = false): Promise<void> => {
        if (this._datas[storeKey]) {
            if (!skipEvent)
                this._emitDataRetrieved(storeKey, loyaltyProvider);
            return;
        }

        const dataUrl = `${this._contentFolderUrl}/online/data/online-data-${storeKey}${this._useDataV4 ? "-v4" : ""}.json`;

        try {
            const data = await this._fetchWithRetry(() => this._loadStoreDataAsync(dataUrl), 3000, 2);
            if (data) {
                const mappedData = this._mapData(data);
                if (mappedData)
                    this._datas[storeKey] = mappedData;

                this._setStoreCountry(storeKey);
                this._getSpecialPremodifierIds(storeKey);
                //Set Timeoffset for store
                this._datas[storeKey].TimeOffset = await this._loadTimeOffsetAsync(storeKey);

                //Set Wait Times for store
                await this._loadWaitTimesAsync(storeKey);

                if (!skipEvent) {
                    this._emitDataRetrieved(storeKey, loyaltyProvider);
                    this._eventBus.emit(EventTypes.STORE_CHANGED, storeKey, loyaltyProvider);
                }
            } else {
                this._eventBus.emit(EventTypes.CLEAR_CART, storeKey);
                this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS);
            }
        } catch (error) {
            this._handleError("LoadData -> LoadStoreData", error);
            this._eventBus.emit(EventTypes.CLEAR_CART, storeKey);
        }

        // Handle calorie data in the background
        this._fetchCalorieDataInBackground(storeKey);
    }

    private async _loadStoreDataAsync(dataUrl: string): Promise<any> {
        try {
            const fullUrl = `${dataUrl}?v=${Date.now()}`;
            const response = await fetch(fullUrl);
            if (!response.ok)
                return null;

            return await response.json();
        } catch {
            return null;
        }
    }

    private readonly _fetchCalorieDataInBackground = async (storeKey: string): Promise<void> => {
        try {
            const calorieData = await this._fetchWithRetry(() => this._loadCalorieDataAsync(storeKey), 10000, 5);

            if (calorieData) {
                this._mapCalorieData(storeKey, calorieData);
            }
        } catch (error) {
            this._handleError("LoadData -> LoadCalorieData", error);
        }
    }

    private readonly _emitDataRetrieved = (storeKey: string, loyaltyProvider: ILoyaltyProvider): void => {
        if (this._datas[storeKey]) {
            this._eventBus.emit(EventTypes.DATA_RETRIEVED, loyaltyProvider);
        }
    }

    private async _fetchWithRetry<T>(fetchFn: () => Promise<T | null>, retryInterval: number, maxRetries: number): Promise<T | null> {
        let attempts = 0;

        while (attempts < maxRetries) {
            try {
                const result = await fetchFn();
                if (result !== null)
                    return result;
            } catch (error) {
                // Log error or debug if needed for specific retries
            }

            attempts++;
            await this._delay(retryInterval);
        }

        return null;
    }

    private readonly _delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

    private readonly _handleError = (context: string, error: unknown): void => {
        if (error instanceof Error) {
            //Util.LogError(`${context}; isMobile: ${localStorage.MobileView}`, error, this._aOLO);
        }
    }

    private readonly _loadCalorieDataAsync = async (storeKey: string): Promise<null | any[]> => {
        const storeData = this._datas[storeKey];
        if (!storeData)
            return [];

        if (this._calories[storeData.StoreInfo.menuGroupId])
            return this._calories[storeData.StoreInfo.menuGroupId];

        const dataCaloriesUrl = `${this._contentFolderUrl}/data/online-calorie-data-${storeData.StoreInfo.menuGroupId}.json`;

        const fullUrl = `${dataCaloriesUrl}?v=${Date.now()}`;
        const response = await fetch(fullUrl);
        if (!response.ok)
            return null;

        try {
            return await response.json();
        } catch {
            return [];
        }
    }

    public httpRequestAsync(method: string, url: string, payload: object | null = null, options?: { showProgress?: boolean, noCache?: boolean }): Promise<Response> {
        const storeKey = this.getProperty("StoreInfo")?.storeKey
            ?? this.getBrandProperty("StoreInfo").storeKey;

        const timeOffset = this.getProperty("TimeOffset")
            ?? this.getBrandProperty("TimeOffset");

        const businessStartMinute = this.getProperty("Settings")?.BSM
            ?? this.getBrandProperty("Settings").BSM;

        let businessDate = new Date().toISOString();
        try {
            businessDate = Util.GetBUSDate(Util.NowStore(timeOffset), timeOffset, businessStartMinute).toISOString();
        } catch { }

        return this._http.request(method, url, payload, storeKey, businessDate, { showProgress: options?.showProgress, noCache: options?.noCache });
    }

    private async _getServerTimeAsync(storeKey: string): Promise<IDataServerTime | null> {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}get-server-date`, { SKY: storeKey });
        return (response.status == 200) ? await this._http.getJson(response) : null;
    }

    private async _getWaitTimesAsync(storeKey: string): Promise<ITimes | null> {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}get-wait-times`, { SKY: storeKey });

        if (response.status != 200) return null;

        const stringResponse = await this._http.getText(response);

        if (stringResponse)
            return JSON.parse(stringResponse) as ITimes;

        return null;
    }

    private readonly _loadTimeOffsetAsync = async (storeKey: string): Promise<number> => {
        const serverTime = await this._getServerTimeAsync(storeKey);
        if (serverTime) {
            const iDate = new Date();
            const clientUtcMilliseconds = iDate.getTime();
            const ClientMSDiff = serverTime.utcMilliseconds - clientUtcMilliseconds;
            const clientUtcOffset = iDate.getTimezoneOffset();
            return (clientUtcOffset + serverTime.storeUtcOffset) * 60000 + ClientMSDiff;
        }

        return 0;
    }

    public async loadStoreTimeOffsetAsync(storeKey: string): Promise<number> {
        if (this._datas[storeKey]?.TimeOffset !== undefined) {
            return this._datas[storeKey].TimeOffset;
        }

        if (this._timeOffsetCache[storeKey] !== undefined) {
            return this._timeOffsetCache[storeKey];
        }

        const timeOffset = await this._loadTimeOffsetAsync(storeKey);

        if (this._datas[storeKey]) {
            this._datas[storeKey].TimeOffset = timeOffset;
        } else {
            this._timeOffsetCache[storeKey] = timeOffset;
        }

        return timeOffset;
    }

    private readonly _loadWaitTimesAsync = async (storeKey: string): Promise<void> => {
        const storeData = this._datas[storeKey];
        if (!storeData)
            return;

        const result = await this._getWaitTimesAsync(storeKey);
        if (!result)
            return;

        this._datas[storeKey].WaitTimes = {
            LRT: result.LRT,
            SET: result.SET,
            DEFWT: result.DEFWT?.map((dwt: any) => ({
                OTID: dwt.OTID,
                WT: dwt.WT
            })) || [],
            WTIM: result.WTIM?.map((wtim: any) => ({
                MIN: wtim.MIN,
                DELMIN: wtim.DELMIN,
                MAKEMIN: wtim.MAKEMIN
            })) || [],
            DELMIN: result.DELMIN,
            INSMIN: result.INSMIN
        };
    }

    private _getDataDefaultValue<K extends keyof IData>(property: K): IData[K] {
        // Fallback type map for default values
        const fallbackTypes: { [key in keyof IData]: IData[key] } = {
            DesktopBanners: [],
            OnlineBanners: [],
            Categories: [],
            Challenges: [],
            Channels: [],
            Cities: "",
            Countries: [],
            Coupons: [],
            DefaultPriceGroupId: 0,
            Donations: [],
            Fundraisers: [],
            Holidays: [],
            Hours: [],
            Info: "",
            Instructions: [],
            Items: [],
            LoyaltyItems: [],
            MembershipTiers: [],
            ModifierCategories: [],
            ModifierDisplayGroups: [],
            Modifiers: [],
            OrderTypeSubTypes: [],
            PreModifiers: [],
            Rewards: [],
            StoreBusinessDate: "",
            Settings: {
                ADDRLOOKUP: 0,
                BSM: 0,
                CCI: false,
                CGID: 0,
                CLESS: 0,
                CPNLIM: 0,
                CRGMIL: 0,
                CURBSD: 0,
                DSZID: 0,
                EMILES: 0,
                FPT: "",
                HCW: false,
                HCWM: false,
                HPK: "",
                HHP: 0,
                HML: false,
                HMLM: false,
                HPRP: false,
                ISCC: false,
                LGID: 0,
                MDCRGMIL: 0,
                NCDG: [],
                OOC: false,
                OOS: {
                    customizeItemMode: 0,
                    couponDisplayType: 0,
                    mealDealLocation: MealDealLocationUiOption.MEAL_DEAL_AS_TAB,
                    showCouponCodeBox: false,
                    birthdayField: BirthdayRequirement.DO_NOT_SHOW,
                    marketingOption: EmailTextMarketingType.NONE,
                    showAgeVerification: false,
                    showNextOrderCredit: false,
                    showSurveyOption: false,
                    showEmailConfirmationField: false,
                    autocompleteGoogleMaps: false,
                    autocompleteProvider: AutocompleteProvider.GOOGLE,
                    excludeWaitTimeFromHours: false,
                    googleTagManager: "",
                    termsOfUseUrl: "",
                    privacyPolicyUrl: "",
                    allowGratuity: false,
                    throttlingTimeWindow: 5,
                    checkoutPageMessage: "",
                    curbsideMessage: "",
                    suggestedTips: [],
                    togoSupplies: [],
                    showAppBanner: false,
                    appBannerPrompText: "",
                    appBannerAppStoreId: "",
                    appBannerPlayStorePackageName: "",
                    autoRotateBanners: false,
                    bannerRotationInterval: 0,
                    showDeliveryStoreAddress: false,
                    homepageRedirectUrl: "",
                    storeLocatorRedirectUrl: "",
                    appTermsOfUseUrl: "",
                    appPrivacyPolicyUrl: "",
                    biometric: false,
                    showMfa: false,
                },
                LOCSET: {
                    googleApiKey: "",
                    hereApiKey: "",
                    mapboxApiKey: "",
                    mapProvider: null,
                    autocompleteProvider: null,
                    autocompleteProviderFailover: null,
                    geocodingProvider: null,
                    geocodingProviderFailover: null,
                },
                OPO: [],
                PBT: 0,
                PMODDM: 0,
                STAT: "",
                STID: 0,
                TPDS: false,
                TPDM: 0,
                SIGNINOPT: 0
            },
            Sizes: [],
            ServiceCharges: [],
            Stores: [],
            SubCategories: [],
            Taxes: [],
            ZipCodes: "",
            DeliveryZones: [],
            ZipTaxes: [],
            OpenPayMerchant: undefined,
            Merchants: [],
            OPDevSId: "",
            OPSourceId: "",
            StoreInfo: {
                storeId: 0,
                storeKey: "",
                storeHex: "",
                storeKeyEncoded: "",
                storeCode: 0,
                brandId: "",
                streetNumber: "",
                address1: "",
                address2: "",
                address3: "",
                address4: "",
                address5: "",
                city: "",
                state: "",
                zip: "",
                countryId: 1,
                latitude: 0,
                longitude: 0,
                phone: "",
                menuGroupId: 1001,
                country: {
                    CountryID: 1,
                    Name: "United States of America",
                    IsoCode: "US",
                    CountryCode: "+1",
                    UnitOfMeasurement: "Imperial",
                    DefaultCurrencySymbol: "$",
                    DefaultCultureCode: "en-us",
                    CurrencyCode: "USD"
                }
            },
            UISettings: {
                showSvgLocationIcons: true
            },
            Notification: {
                hasNotifications: false,
                hasTextNotifications: false,
                hasEmailNotifications: false
            },
            DefaultPreModifierId: 0,
            NonePreModifierId: 0,
            TimeOffset: 0,
            WaitTimes: {
                LRT: 0,
                SET: 0,
                DEFWT: [],
                DELMIN: 0,
                INSMIN: 0,
            },
            RedeemItemDisplayGroups: [],
            CashRoundingRule: {
                roundingRule: RoundingRule.DO_NOT_ROUND,
                roundTo: 0
            }
        };

        return fallbackTypes[property];
    }

    private _mapOnlineOrderingSettingSetting(data: any): IOnlineOrderingSettings {
        return {
            customizeItemMode: data.SETS?.OOS?.customizeItemMode ?? 0,
            couponDisplayType: data.SETS?.OOS?.couponDisplayType ?? 0,
            mealDealLocation: data.SETS?.OOS?.mealDealLocation ?? MealDealLocationUiOption.MEAL_DEAL_AS_TAB,
            showCouponCodeBox: Boolean(data.SETS?.OOS?.showCouponCodeBox),
            birthdayField: data.SETS?.OOS?.birthdayField ?? BirthdayRequirement.DO_NOT_SHOW,
            marketingOption: data.SETS?.OOS?.marketingOption ?? EmailTextMarketingType.NONE,
            showAgeVerification: Boolean(data.SETS?.OOS?.showAgeVerification),
            showNextOrderCredit: Boolean(data.SETS?.OOS?.showNextOrderCredit),
            showSurveyOption: Boolean(data.SETS?.OOS?.showSurveyOption),
            showEmailConfirmationField: Boolean(data.SETS?.OOS?.showEmailConfirmationField),
            autocompleteGoogleMaps: Boolean(data.SETS?.OOS?.autocompleteGoogleMaps),
            autocompleteProvider: data.SETS?.OOS?.autocompleteProvider ?? AutocompleteProvider.GOOGLE,
            excludeWaitTimeFromHours: Boolean(data.SETS?.OOS?.excludeWaitTimeFromHours),
            googleTagManager: data.SETS?.OOS?.googleTagManager ?? "",
            termsOfUseUrl: data.SETS?.OOS?.termsOfUseUrl ?? "",
            privacyPolicyUrl: data.SETS?.OOS?.privacyPolicyUrl ?? "",
            allowGratuity: Boolean(data.SETS?.OOS?.allowGratuity),
            throttlingTimeWindow: data.SETS?.OOS?.throttlingTimeWindow ?? 5,
            checkoutPageMessage: data.SETS?.OOS?.checkoutPageMessage ?? "",
            curbsideMessage: data.SETS?.OOS?.curbsideMessage ?? "",
            suggestedTips: data.SETS?.OOS?.suggestedTips ?? [],
            togoSupplies: data.SETS?.OOS?.togoSupplies ?? [],
            showAppBanner: Boolean(data.SETS?.OOS?.showAppBanner),
            appBannerPrompText: data.SETS?.OOS?.appBannerPrompText ?? "",
            appBannerAppStoreId: data.SETS?.OOS?.appBannerAppStoreId ?? "",
            appBannerPlayStorePackageName: data.SETS?.OOS?.appBannerPlayStorePackageName ?? "",
            autoRotateBanners: data.SETS?.OOS?.autoRotateBanners ?? false,
            bannerRotationInterval: data.SETS?.OOS?.bannerRotationInterval ?? 0,
            showDeliveryStoreAddress: data.SETS?.OOS?.showDeliveryStoreAddress ?? false,
            homepageRedirectUrl: data.SETS?.OOS?.homepageRedirectUrl ?? "",
            storeLocatorRedirectUrl: data.SETS?.OOS?.storeLocatorRedirectUrl ?? "",
            appTermsOfUseUrl: data.SETS?.OOS?.appTermsOfUseUrl ?? "",
            appPrivacyPolicyUrl: data.SETS?.OOS?.appPrivacyPolicyUrl ?? "",
            biometric: Boolean(data.SETS?.OOS?.biometric),
            showMfa: Boolean(data.SETS?.OOS?.showMfa),
        };
    }

    private _mapLocationSettings(data: any): ILocationSettings {
        const locationSettings = data.SETS?.LOCSET;
        return {
            googleApiKey: locationSettings?.GoogleApiKey ?? "",
            hereApiKey: locationSettings?.HereApiKey ?? "",
            mapboxApiKey: locationSettings?.MapboxApiKey ?? "",
            mapProvider: this._parseMapProvider(locationSettings?.MapProvider),
            autocompleteProvider: this._parseGeoServiceProviderName(locationSettings?.AutocompleteProvider),
            autocompleteProviderFailover: this._parseGeoServiceProviderName(locationSettings?.AutocompleteProviderFailover),
            geocodingProvider: this._parseGeoServiceProviderName(locationSettings?.GeocodingProvider),
            geocodingProviderFailover: this._parseGeoServiceProviderName(locationSettings?.GeocodingProviderFailover),
        };
    }

    private _parseMapProvider(value: string): MapProvider | null {
        switch (value?.trim().toLowerCase()) {
            case "google":
                return MapProvider.GOOGLE;
            case "here":
                return MapProvider.HERE;
            case "mapbox":
                return MapProvider.MAPBOX;
            default:
                return null;
        }
    }

    private _parseGeoServiceProviderName(value: string): GeoServiceProviderName | null {
        switch (value?.trim().toLowerCase()) {
            case "adora":
                return "adora";
            case "google":
                return "google";
            case "here":
                return "here";
            case "mapbox":
                return "mapbox";
            case "geocodio":
                return "geocodio";
            default:
                return null;
        }
    }

    private _filterDuplicateFundraiserCodes<T extends { Code?: string }>(fundraisers: T[]): T[] {
        const codeCounts = new Map<string, number>();
        for (const f of fundraisers) {
            const key = (f.Code ?? "").trim().toUpperCase();
            if (key) codeCounts.set(key, (codeCounts.get(key) ?? 0) + 1);
        }
        return fundraisers.filter(f => {
            const key = (f.Code ?? "").trim().toUpperCase();
            return !key || codeCounts.get(key) === 1;
        });
    }

    private readonly _mapData = (data: any, isBrand: boolean = false): IData | null => {
        try {
            const settings: IDataSettings = data.SETS ?? {};
            settings.OOS = this._mapOnlineOrderingSettingSetting(data);
            settings.LOCSET = this._mapLocationSettings(data);
            settings.OPO = data.SETS.OPO.Values?.map((x: any) => ({
                OrderTypeID: x.OTID,
                PayInStore: x.PIS,
                PayInStoreLimit: x.PISL,
                PayInStoreAlternate: x.PISA,
                Cash: x.CSH,
                CashLimit: x.CSHL,
                CashAlternate: x.CSHA,
                CardOnFile: x.COF,
                CardOnFileCvvOption: x.COFC,
                CardOnFileCvvThreshold: x.COFCT,
                CreditCard: x.CC,
                CreditCardOption: x.CCO,
                CreditCardCvvOption: x.CCCO,
                CreditCardCvvThreshhold: x.CCCT,
                CreditCardAvsOption: x.CCAO,
                CreditCardAvsThreshhold: x.CCAT,
                CreditCardPreValidationOption: x.CCPVO,
                CreditCardPreValidationThreshhold: x.CCPVOT,
                GooglePay: x.GPAY,
                ApplePay: x.APAY
            })) ?? [];

            const banners = isBrand ? data.DBAN?.map((x: any) => ({
                Id: x.ID,
                Banners: x.Data?.map((banner: any) => ({
                    CouponID: banner.LCID,
                    CouponNames: banner?.LCNAMs,
                    ImageURL: banner.IURL
                })) || [],
                RotationActive: x.RIA || false,
                RotationPeriod: x.RP || 1,
                SlideIdx: 1
            })) || [] : [];

            const onlineBanners = isBrand ? this._mapOnlineBannersFromBrandData(data.OBAN) : [];

            const newData: IData = {
                DesktopBanners: banners,
                OnlineBanners: onlineBanners,
                Categories: data.CATS?.map((cat: any) => ({
                    Default: cat.DEF,
                    Descriptions: cat.DSCRs,
                    Index: cat.IDX,
                    ImageUrl: cat.IMGU,
                    IsMealDeal: cat.ISMD,
                    ItemWebCategoryId: cat.IWCID,
                    Names: cat.NAMs,
                    Upsale: cat.UPS
                })) || [],
                Challenges: data.CLGs?.map((clg: any) => ({
                    ChallengeId: clg.ID,
                    Names: clg.NAMs,
                    StartDate: new Date(Util.formatDateFunc(clg.SDT)),
                    EndDate: new Date(Util.formatDateFunc(clg.EDT)),
                    Members: clg.MEMs?.map((mem: any) => ({
                        MemberId: mem.MID,
                        Name: mem.NAM,
                        Events: mem.EVTs?.map((evt: any) => ({
                            EventId: evt.EID,
                            EventName: evt.ENAM,
                            EventDate: new Date(Util.formatDateFunc(evt.EDATE)),
                            FundraisingPercentage: evt.FUND,
                            IsAllowCoupons: evt.IACPN,
                            IsSubMemberField: evt.ISMEM,
                            SubMemberTitle: evt.SMEMTL,
                            EntryTypeId: evt.ETID,
                            IsRequired: evt.IREQ,
                            IsMultipleSelectionsAllowed: evt.IMSALL,
                            SubMembers: evt.SMEMs?.map((smem: any) => ({
                                SubMemberId: smem.SMID,
                                SubMemberName: smem.SMNAM,
                                IsExtraField: smem.IEFLD,
                                ExtraFieldTitle: smem.EFLDTL,
                                EntryTypeId: smem.ETID,
                                IsRequired: smem.IREQ,
                                IsMultipleSelectionsAllowed: smem.IMSALL,
                                PredefinedValues: smem.PVALS
                            })) || []
                        })) || []
                    })) || []
                })) || [],
                Channels: data.CHLS?.map((chl: any) => ({
                    ChannelID: chl.CHID,
                    ChannelName: chl.NAM,
                    PromoCodePrefix: chl.PCP
                })) || [],
                Cities: data.CITIS || "",
                Countries: data.COUNTRIES?.map((country: any) => ({
                    CountryID: country.CountryID,
                    Name: country.Name,
                    IsoCode: country.IsoCode,
                    CountryCode: country.CountryCode,
                    UnitOfMeasurement: country.UnitOfMeasurement,
                    DefaultCurrencySymbol: country.DefaultCurrencySymbol,
                    DefaultCultureCode: country.DefaultCultureCode,
                    CurrencyCode: country.CurrencyCode
                })) || [],
                Coupons: data.CPNS?.map((cpn: any) => ({
                    CouponId: cpn.CPID,
                    CouponTypeId: cpn.CPTID,
                    AllowOtherCoupons: cpn.AOC,
                    ExcludeLoyaltyUsage: cpn.ELU,
                    AutoApplyExactMatch: cpn.AAEXCMCH,
                    BackgroundColor: cpn.TBGC,
                    BOGOSelectionTypeID: cpn.BGSTID,
                    Descriptions: cpn.DSCRs,
                    Discount: cpn.DIC,
                    ExactMatch: cpn.EXCMCH,
                    ImageURL: cpn.IURL,
                    Images: cpn.IMG ? {
                        Thumbnail: cpn.IMG?.THUMB,
                        Full: cpn.IMG?.FULL,
                        DesktopLanding: cpn.IMG?.LAND,
                        AppCategories: cpn.IMG?.APPCAT,
                        AppLanding: cpn.IMG?.APPLAND
                    } : null,
                    IsBeforeTax: cpn.IBTX,
                    IsBOGO: cpn.BOGO,
                    IsHiddenOnline: cpn.IHDN,
                    IsPriceReducer: cpn.IPR,
                    IsProfileRequired: cpn.IOPR,
                    IsScheduled: cpn.SCH,
                    IsVerificationNeeded: cpn.IVN,
                    IsAutoApply: cpn.IAA,
                    IsAppOnly: cpn.IAPPO,
                    LimitsPerOrder: cpn.LPO,
                    MaximumDiscountAmount: cpn.MXAMT,
                    MinimumItemQuantity: cpn.MINITMQTY,
                    MinimumOrderAmount: cpn.MIAMT,
                    CalculationMethodID: cpn.CALCID,
                    Names: cpn.NAMs,
                    Priority: cpn.PRITY,
                    WebCategoryId: cpn.WCID,
                    StartDate: cpn.SDATE,
                    EndDate: cpn.EDATE,
                    Codes: cpn.CCODS?.map((cd: any) => ({
                        CouponCode: cd.CCOD
                    })) || [],
                    Items: cpn.CITMS?.map((itm: any) => ({
                        AddOnPrice: itm.AOPRC,
                        GroupIndex: itm.GRP,
                        ItemId: itm.IID,
                        ItemIndex: itm.IIDX,
                        ModifierCount: itm.MCNT,
                        Quantity: itm.QTY,
                        SizeId: itm.SZID
                    })) || [],
                    ItemCategories: cpn.CITMCATS?.map((cat: any) => ({
                        CategoryId: cat.CATID
                    })) || [],
                    OrderTypeSubTypes: cpn.OTSTS?.map((ot: any) => ({
                        OrderTypeId: ot.OT,
                        OrderTypeSubId: ot.OST
                    })) || [],
                    Schedules: cpn.SCHS?.map((sch: any) => ({
                        WeekDayNumber: sch.WKDNO,
                        IsActive: sch.ISACT,
                        StartDayMinute: sch.STRTMIN,
                        EndDayMinute: sch.ENDMIN
                    })) || [],
                    Upgrades: cpn.UGRDS?.map((upg: any) => ({
                        BuySizeId: upg.BSZID,
                        PriceSizeId: upg.PSZID,
                        UpgradeIndex: upg.UGRDIDX
                    })) || []
                })) || [],
                DefaultPriceGroupId: data.DEFPGID,
                Donations: data.DON?.map((don: any) => ({
                    Amount1: don.AMT1,
                    Amount2: don.AMT2,
                    Amount3: don.AMT3,
                    Description: don.DES,
                    DonationId: don.DID,
                    ImageUrl: don.IMG,
                    IsRounded: don.IRND,
                    Names: don.NAM,
                    RedirectURL: don.URL,
                    Threshold: don.TH
                })) || [],
                Fundraisers: this._filterDuplicateFundraiserCodes(data.FUN?.map((fun: any) => ({
                    FundraiserId: fun.FID,
                    Name: fun.NAM,
                    StartDateTime: fun.SDAT,
                    EndDateTime: fun.EDAT,
                    Code: fun.CDE,
                    IsCouponAllowed: fun.ICPN,
                    IsCustomField1: fun.ICF1,
                    IsCustomField2: fun.ICF2,
                    CustomField1Names: fun.CF1NAMs,
                    CustomField2Names: fun.CF2NAMs,
                    IsOpen: fun.OPEN
                })) || []),
                Holidays: data.HDAYS?.map((hol: any) => ({
                    HolidayId: hol.HID,
                    HolidayStatusId: hol.HSID,
                    StartMinute: hol.SMIN,
                    EndMinute: hol.EMIN,
                    Info: hol.THRINFO ? JSON.parse(hol.THRINFO) : null,
                    Names: hol.NAMs
                })) || [],
                Hours: data.HUR?.map((hr: any) => ({
                    OrderTakingStartDayMinute: hr.OTSDM,
                    OrderTakingEndDayMinute: hr.OTEDM,
                    OpenDayMinute: hr.ODM,
                    CloseDayMinute: hr.CDM
                })) || [],
                Info: data.INFO,
                Instructions: data.INST?.map((com: any) => ({
                    CommentId: com.COMMID,
                    Items: com.ITM?.map((itm: any) => ({
                        ItemWebCategoryId: itm.ICID,
                        SizeId: itm.SID
                    })) || [],
                    Names: com.TEXT,
                    SpecialInstructionsId: com.SICID,
                    SpecialInstructionsNames: com.SICNAMs || [],
                    IsMultiSelect: com.SICIMS || false
                })) || [],
                Items: data.ITMS?.map((itm: any, index: number) => ({
                    ItemId: itm.IID,
                    Index: index,
                    ItemCategoryId: itm.ICID,
                    WebCategoryId: itm.IWCID,
                    WebCategoryIndex: itm.WCIDX,
                    SubCategoryId: itm.ISCID,
                    SubCategoryIndex: itm.SCIDX,
                    IsDefault: itm.IDEF,
                    IsHalfable: itm.IHAF,
                    CssClassName: itm.BCOL,
                    ImageURL: itm.IMG,
                    MinimumModifierCount: itm.MINMCNT,
                    MaximumModifierCount: itm.MAXMCNT,
                    PrepTime: itm.PTIM,
                    MakeTime: itm.MTIM,
                    CookTime: itm.CTIM,
                    IsBuildYourOwn: itm.IBYO,
                    IsGiftCard: itm.IGCD,
                    IsGiftCert: itm.IGCT,
                    IsLoyalty: itm.ILOY,
                    IsCutWrap: itm.ISCR,
                    IsMakeLine: itm.ISML,
                    CalorieDisplayId: itm.CALDID,
                    MinimumCustomerAge: itm.AGE,
                    Names: itm.INAMS,
                    Descriptions: itm.IDSCRS,
                    ServingNames: itm.SNAMS,
                    ModifierDisplayGroups: itm.MODGs?.map((mdg: any) => ({
                        ModifierDisplayGroupId: mdg.MDGRPID,
                        Index: mdg.IDX,
                        Modifiers: mdg.MODS?.map((mod: any) => ({
                            AddsToPrice: mod.ATPRC,
                            CountForPricing: mod.CFPRC,
                            CountForPricingByItself: mod.CFPRCBI,
                            IsDefault: mod.IDEF,
                            IsDisabled: false,
                            Index: mod.IDX,
                            IsHiddenOnOrder: mod.IHOORD,
                            ModifierId: mod.MID,
                            Name: mod.NAMS,
                            MergedModifierDisplayGroupId: mod.MMDGID,
                            ModifierWebCategoryId: mod.MWCID,
                            ModifierWebCategoryIndex: mod.MWCIDX,
                            PreModifierException: mod.XPMOD,
                            SortingIndex: mod.SIDX,
                            Calories: [],
                            Prices: mod.MPRCS?.map((prc: any) => ({
                                SizeId: prc.SZID,
                                AddOnPrice: prc.APRC
                            })) || []
                        })) || []
                    })) || [],
                    OrderTypes: itm.OTSS?.map((ot: any) => ({
                        CutWrap: ot.CWP,
                        Label: ot.LBL,
                        Make: ot.MAK,
                        OrderTypeId: ot.OTID,
                        Prep: ot.PRP,
                        Print: ot.PRT,
                        Sizes: ot.SIZS?.map(this._mapItemOrderTypeSize) || [],
                        HiddenSizes: ot.HSIZS?.map(this._mapItemOrderTypeSize) || [],
                    })) || [],
                    SizeCalories: itm.SIZCALS?.map((scal: any) => ({
                        MaximumCalories: scal.MXCALS,
                        MinimumCalories: scal.MICALS,
                        Servings: scal.SRVS,
                        SizeId: scal.SZID
                    })) || [],
                    Prices: itm.PRCS?.map((prc: any) => ({
                        MinimumPrice: prc.MIPRC,
                        MaximumPrice: prc.MXPRC,
                        PriceGroupId: prc.PGID,
                        PerModifierPrice: prc.PMPRC,
                        TaxIncluded: prc.TXIC,
                        Price: prc.PRC,
                        SizeId: prc.SZID
                    })) || [],
                    Taxes: itm.TAXS?.map((tax: any) => ({
                        TaxId: tax.TID,
                        Index: tax.IDX
                    })) || [],
                    UnavailableDefaultModifiers: itm.UDMOD?.map((udmod: any) => ({
                        ModifierId: udmod.MID,
                        Names: udmod.NAMS
                    })) || []
                })) || [],
                LoyaltyItems: data.LOYITMS?.map((itm: any) => ({
                    ItemId: itm.IID,
                    SizeId: itm.SID,
                    Points: itm.PON,
                    IsChargeable: itm.ICHR
                })) || [],
                MembershipTiers: data.MEMs?.map((mem: any) => ({
                    MembershipId: mem.ID,
                    LifetimePoints: mem.LPTS,
                    Names: mem.NAMs,
                    Rewards: mem.RIDs?.map((rew: any) => ({
                        MembershipId: rew.ID,
                        RewardId: rew.RID
                    })) || [],
                })) || [],
                ModifierCategories: data.MODCATS?.map((mcat: any) => ({
                    ModifierWebCategoryId: mcat.MWCID,
                    Descriptions: mcat.DSCRs,
                    Index: mcat.IDX,
                    Names: mcat.NAMs
                })) || [],
                ModifierDisplayGroups: data.MDGS?.map((mdg: any) => ({
                    ModifierDisplayGroupId: mdg.MDGRPID,
                    IsWeightChangeable: mdg.IWCHNG,
                    IsHalfable: mdg.IHAF,
                    IsVisible: mdg.ISVSB,
                    MaxSelectionCount: mdg.MAXCNT,
                    MinSelectionCount: mdg.MINCNT,
                    MustToggle: mdg.MTGL,
                    Names: mdg.NAMs
                })) || [],
                Modifiers: data.MODS?.map((mod: any) => ({
                    ModifierId: mod.modID,
                    Descriptions: mod.DSCRs,
                    ImageUrl: mod.IMGU,
                    Index: mod.IDX,
                    IsCrust: mod.ICRST,
                    ModifierWebCategoryId: mod.MWCID,
                    Names: mod.NAMs,
                    SortingIndex: mod.SIDX
                })) || [],
                OrderTypeSubTypes: data.OTSTS?.map((otst: any) => ({
                    OrderTypeId: otst.OTID,
                    OrderTypeSubId: otst.OSTID,
                    PriceGroupId: otst.PGID,
                    Alias: otst.ALI,
                    AskCustomerInfo: otst.ACINF,
                    AskCustomerAddress: otst.ACADR,
                    DefaultPay: otst.DPAY,
                    EndMinute: otst.EMIN,
                    FutureOrders: otst.FUO,
                    MinAmount: otst.MAMT,
                    Names: otst.TNAMs,
                    OrderTypeCharge: otst.OTCH,
                    OrderTypeChargeTaxId: otst.TXID,
                    PausedUntil: otst.PT,
                    Rate: otst.OTTXR,
                    StartMinute: otst.SMIN,
                    TaxTypeId: otst.TTID,
                    TaxName: otst.TXNAM,
                    WaitTime: otst.WT,
                    SortingIndex: otst.SIDX
                })) || [],
                PreModifiers: data.PMODS?.map((pmod: any) => ({
                    PreModifierId: pmod.PMID,
                    Descriptions: pmod.DSCRs,
                    InventoryMultiplier: pmod.ICMUL,
                    IsDefault: pmod.IDEF,
                    IsNone: pmod.INON,
                    IsHidden: pmod.IHID,
                    IsDeleted: pmod.IDLT,
                    Names: pmod.NAMs,
                    PriceCountMultiplier: pmod.PCMUL
                })) || [],
                Rewards: data.REWs?.map((rew: any) => ({
                    RewardId: rew.ID,
                    Points: rew.PTS,
                    IsTierBased: rew.ITB,
                    IsTierUpgrade: rew.ITU,
                    FrequencyId: rew.FID,
                    Names: rew.NAMs
                })) || [],
                StoreBusinessDate: data.SBDAT,
                Settings: settings,
                Sizes: data.SZS?.map((size: any) => ({
                    SizeId: size.SZID,
                    Descriptions: size.DSCRs,
                    Index: size.IDX,
                    Names: size.NAMs
                })) || [],
                ServiceCharges: data.SRVCHRG?.map((srvchg: any) => ({
                    ServiceChargeId: srvchg.SCHID,
                    FixAmount: srvchg.FIXAMT,
                    IncludesDeliveryCharge: srvchg.INCDC,
                    IncludesSubtotal: srvchg.INCST,
                    IncludesTax: srvchg.INCTX,
                    MaxAmount: srvchg.MAXAMT,
                    MaxCharge: srvchg.MAXCHRG,
                    MinAmount: srvchg.MINAMT,
                    MinCharge: srvchg.MINCHRG,
                    Names: srvchg.NAM,
                    Percentage: srvchg.PERCNT,
                    TaxId: srvchg.TAXID,
                    TaxName: srvchg.TAXNAM,
                    TaxRate: srvchg.TAXRAT,
                    TaxTypeId: srvchg.TAXTID,
                    OrderTypeSubTypes: srvchg.OTSTS?.map((ot: any) => ({
                        OrderTypeId: ot.OTID,
                        OrderTypeSubId: ot.OTSID,
                    })) || []
                })) || [],
                Stores: data.STRS?.map((str: any) => ({
                    StoreId: str.SID,
                    Color: str.COL,
                    Name: str.NAM,
                    SortingIndex: str.SNO,
                    Nickname: str.NNAM,
                    Address1: str.ADD1,
                    City: str.CT,
                    State: str.ST,
                    ZipCode: str.ZP,
                    Latitude: str.LT,
                    Longitude: str.LG,
                    Heartland: str.HPK,
                    Items: str.AITM,
                    Distance: 0,
                    Holidays: str.HDAYS,
                    Taxes: str.TAX?.map((tax: any) => ({
                        FixedAmount: tax.FAMT,
                        Rate: tax.RAT,
                        SizeId: tax.SZID,
                        TaxId: tax.TID
                    })) || [],
                    WaitTimes: str.WTS?.map((wt: any) => ({
                        FutureOrders: true,
                        OrderTypeId: wt.OTID,
                        WaitTime: wt.WT
                    })) || []
                })) || [],
                SubCategories: data.SUBCATS?.map((scat: any) => ({
                    ItemSubCategoryId: scat.SCID,
                    Descriptions: scat.DSCRs,
                    Index: scat.IDX,
                    Names: scat.NAMs
                })) || [],
                Taxes: data.TAXES?.map((tax: any) => ({
                    TaxId: tax.TID,
                    FixAmount: tax.FAMT,
                    OrderTypesExcluded: JSON.parse(tax.OTEXCs) || [],
                    IsPercentageBase: tax.IPBAS,
                    IsFixedAmountBase: tax.IFAMT,
                    IsSizeBase: tax.ISZBAS,
                    IsAddToSalesAmount: tax.IATSAMT,
                    IsAffectedByDiscounts: tax.IEFFDIS,
                    IsPerOrder: tax.IPORD,
                    IsNextToItemIndividually: tax.DNTITM,
                    Names: tax.NAMs,
                    Rate: tax.TRT,
                    SizeId: tax.SZID,
                    TaxTypeId: tax.TTID
                })) || [],
                ZipCodes: data.ZIPS || "",
                DeliveryZones: data.ZONS?.map((zn: any) => ({
                    ZoneId: zn.ZID,
                    IsLimitedTime: zn.ILT,
                    StoreId: zn.SID,
                    ZoneCharge: zn.ZCHRG,
                    ZoneMinDeliveryAmount: zn.MINDEL,
                    ZoneTypeId: zn.ZTID,
                    Polygons: zn.PGON?.map((pol: any) => ({
                        Latitude: pol.Lt,
                        Longitude: pol.Lg
                    })) || [],
                    LimitedTimes: zn.LIMTIME?.map((tm: any) => ({
                        WeekDay: tm.wDay,
                        StartMinute: tm.sMin,
                        EndMinute: tm.eMin
                    })) || []
                })) || [],
                ZipTaxes: data.ZTAXES?.map((ztax: any) => ({
                    TaxId: ztax.TID,
                    FixAmount: ztax.FAMT,
                    Rate: ztax.RT,
                    SizeId: ztax.SZID,
                    StoreId: ztax.SID,
                    ZipCode: ztax.ZCD
                })) || [],
                OpenPayMerchant: data.OPMCH,
                Merchants: data.MCHPIDS?.map((x: any) => ({
                    ProcessorId: x.pid,
                    TransactionType: x.tType,
                    isGiftCardPin: x.IGCP,
                    isOmniToken: x.IOTK ?? false,
                    isGooglePay: x.IGP ?? false,
                    isApplePay: x.IAP ?? false,
                    gatewayMerchantId: x.GMID ?? ""
                })) || [],
                OPDevSId: "",
                OPSourceId: "",
                StoreInfo: {
                    storeId: data.SINFO?.SID || 0,
                    storeKey: data.SINFO?.SKEY || "",
                    storeHex: data.SINFO?.SHEX || "",
                    storeKeyEncoded: data.SINFO?.SKEYENC || "",
                    storeCode: data.SINFO?.SCODE || "",
                    brandId: data.SINFO?.BID || "",
                    streetNumber: data.SINFO?.SNUM || "",
                    address1: data.SINFO?.ADD1 || "",
                    address2: data.SINFO?.ADD2 || "",
                    address3: data.SINFO?.ADD3 || "",
                    address4: data.SINFO?.ADD4 || "",
                    address5: data.SINFO?.ADD5 || "",
                    city: data.SINFO?.CITY || "",
                    state: data.SINFO?.STA || "",
                    zip: data.SINFO?.ZIP || "",
                    countryId: data.SINFO?.CID || 1,
                    latitude: data.SINFO?.LAT || 0,
                    longitude: data.SINFO?.LON || 0,
                    phone: data.SINFO?.PHN || "",
                    menuGroupId: data.SINFO?.SMGID || 1006,
                    country: {
                        CountryID: data.SINFO?.CID || 1,
                        Name: "United States of America",
                        IsoCode: "US",
                        CountryCode: "+1",
                        UnitOfMeasurement: "Imperial",
                        DefaultCurrencySymbol: "$",
                        DefaultCultureCode: "en-us",
                        CurrencyCode: "USD"
                    }
                },
                Notification: {
                    hasNotifications: data.NOTIF?.INOTIF || false,
                    hasTextNotifications: data.NOTIF?.ITXTNOTIF || false,
                    hasEmailNotifications: data.NOTIF?.IEMLNOTIF || false
                },
                UISettings: {
                    showSvgLocationIcons: true
                },
                DefaultPreModifierId: 0,
                NonePreModifierId: 0,
                TimeOffset: 0,
                WaitTimes: {
                    LRT: 0,
                    SET: 0,
                    DEFWT: [],
                    DELMIN: 0,
                    INSMIN: 0,
                },
                RedeemItemDisplayGroups: data.RITEMDGROUPS?.map((ridg: any) => ({
                    LoyaltyItemDisplayGroupID: ridg.ID,
                    Names: ridg.NAMs,
                    SortingIndex: ridg.SIDX,
                    ImageUrl: ridg.IMGURL || null,
                    MinPoints: ridg.MINPTS,
                    MaxPoints: ridg.MAXPTS,
                    Items: ridg.ITMS?.map((ritem: any) => ({
                        ItemId: ritem.IID,
                        SizeId: ritem.SID,
                        Points: ritem.PTS,
                    } as IDataLoyaltyItem)) ?? []
                } as IDataRedeemItemDisplayGroup)) ?? [],
                CashRoundingRule: {
                    roundingRule: data.CRR?.RR || RoundingRule.DO_NOT_ROUND,
                    roundTo: data.CRR?.RT ?? 0
                }
            };

            return newData;
        }
        catch (error) {
            console.error("Error occurred in mapData:", error);
            return null;
        }
    }

    private readonly _mapCalorieData = (storeKey: string, data: any[]): void => {
        if (data.length == 0)
            return;

        const storeData = this._datas[storeKey];
        if (!storeData)
            return;

        // Create a lookup table
        const lookup = data.reduce((acc: any, cur: any) => {
            if (cur.CALS) {
                const key = `${cur.IID}-${cur.MID}`;
                acc[key] = cur.CALS.map((cal: any) => ({
                    Calories: cal.CALS,
                    ModifierCount: cal.MCNT,
                    PreModifierId: cal.PMID,
                    SizeId: cal.SID
                })) || [];
            }
            return acc;
        }, {});

        this._calories[storeData.StoreInfo.menuGroupId] = lookup;

        // Iterate over items
        for (const item of storeData.Items) {
            for (const mdg of item.ModifierDisplayGroups) {
                for (const mod of mdg.Modifiers) {
                    const key = `${item.ItemId}-${mod.ModifierId}`;
                    if (lookup[key])
                        mod.Calories.push(...lookup[key]);
                }
            }
        }
    }

    private readonly _getSpecialPremodifierIds = (storeKey: string): void => {
        const data = this._datas[storeKey];
        if (!data)
            return;

        const premodifiers = data.PreModifiers;
        if (premodifiers.length === 0)
            return;

        for (const premodifier of premodifiers) {
            if (premodifier.IsDefault)
                data.DefaultPreModifierId = premodifier.PreModifierId;
            else if (premodifier.IsNone)
                data.NonePreModifierId = premodifier.PreModifierId;
        }
    }

    private _setStoreCountry(storeKey: string): void {
        const data = this._datas[storeKey];
        if (!data)
            return;

        const country = data.Countries.find(x => x.CountryID === data.StoreInfo.countryId);
        if (country)
            data.StoreInfo.country = country;
    }

    public mapLocationsData(data: any[]): IStoreLocation[] {
        const mappedData = data.map((x: any) => ({
            storeKey: x.SKEY,
            storeCode: x.SCODE,
            storeId: x.SID,
            name: x.NAM,
            phone: x.PHN,
            streetNumber: x.SNUM,
            address1: x.ADD1,
            address2: x.ADD2,
            address3: x.ADD3,
            address4: x.ADD4,
            address5: x.ADD5,
            city: x.CITY,
            state: x.STA,
            zip: x.ZIP,
            countryId: x.CID,
            latitude: x.LAT,
            longitude: x.LON,
            statusId: x.STATID,
            url: x.OOURL,
            distance: 0,
            deliveryDistance: 0,
            marker: null,
            fullState: "",
            businessStartMinute: x.BSM,
            settings: x.SETTs?.VAL ?? {
                ThirdPartyDelService: false,
                ThirdPartyDeliveryMethod: false
            },
            zones: [],
            cities: x.CITs?.map((y: any) => y.CITY) || [],
            orderTypes: x.OTs?.map((y: any) => y.OTID) || [],
            zipCodes: x.ZIPs?.map((y: any) => y.ZIP) || [],
            holidays: x.HOLs?.map((y: any) => ({
                id: y.HID,
                statusId: y.HSID,
                startMinute: y.SMIN,
                endMinute: y.EMIN
            })) || [],
            hours: x.HRs?.map((y: any) => ({
                weekdayId: y.WID,
                startMinute: y.SMIN,
                endMinute: y.EMIN
            })) || []
        })) || [];

        return mappedData;
    }

    /**
     * Maps online banners from brand data JSON response
     */
    private _mapOnlineBannersFromBrandData(obanData: any[] | null | undefined): IRegionalizedBanner[] {
        if (!obanData || !Array.isArray(obanData)) return [];

        return obanData.map((banner: any) => ({
            onlineBannerID: banner.OBID,
            name: banner.NAM,
            couponID: banner.CPNID ?? null,
            couponNames: this._parseJsonField(banner.CPNNAMS),
            isActive: banner.ISACT,
            showOnBrowser: banner.SHBROW,
            showOnApp: banner.SHAPP,
            sortingIndex: banner.SIDX,
            regionRuleType: banner.RRTYP,
            timeSensitive: banner.TSENS,
            images: this._parseBannerImages(this._parseJsonField(banner.IMGS)),
            schedules: this._parseBannerSchedules(this._parseJsonField(banner.SCHS)),
            regions: this._parseBannerRegions(this._parseJsonField(banner.RGNS))
        }));
    }

    /**
     * Generic helper to parse JSON string fields
     */
    private _parseJsonField<T>(field: string | T | null | undefined): T | null {
        if (!field) return null;

        if (typeof field === "string") {
            try {
                return JSON.parse(field) as T;
            } catch {
                return null;
            }
        }

        return field as T;
    }

    private _parseBannerImages(imgsData: any[] | null | undefined): IBannerImage[] {
        if (!imgsData || !Array.isArray(imgsData)) return [];

        try {
            return imgsData.map((img) => ({
                bannerImageID: img.BIID,
                onlineBannerID: img.OBID,
                sizeCode: img.SZCD,
                imagePath: img.IMGPTH
            }));
        } catch {
            return [];
        }
    }

    private _parseBannerSchedules(schsData: any[] | null | undefined): IBannerSchedule[] {
        if (!schsData || !Array.isArray(schsData)) return [];

        try {
            return schsData.map((sch) => ({
                bannerScheduleId: sch.BSID,
                onlineBannerId: sch.OBID,
                weekDayNo: sch.WKDAY,
                isActive: sch.ISACT,
                startMinute: sch.SMIN,
                endMinute: sch.EMIN
            }));
        } catch {
            return [];
        }
    }

    private _parseBannerRegions(rgnsData: any[] | null | undefined): IBannerRegion[] {
        if (!rgnsData || !Array.isArray(rgnsData)) return [];

        try {
            return rgnsData.map((rgn) => ({
                regionZoneID: rgn.RZID,
                zoneName: rgn.ZNAM || '',
                points: this._parseRegionPoints(this._parseJsonField(rgn.POINTS))
            }));
        } catch {
            return [];
        }
    }

    private _parseRegionPoints(pointsData: any[] | null | undefined): IRegionZonePoint[] {
        if (!pointsData || !Array.isArray(pointsData)) return [];

        try {
            return pointsData
                .map((pt) => ({
                    pointIndex: pt.PIDX,
                    latitude: pt.LAT,
                    longitude: pt.LON
                }))
                .sort((a, b) => a.pointIndex - b.pointIndex);
        } catch {
            return [];
        }
    }

    /**
     * Maps raw loyalty payload data into the client loyalty model.
     *
     * @param {LoyaltyInfoRaw} loyaltyInfo - Raw loyalty data received from the backend.
     * @returns {DataLoyalty} The normalized loyalty model used by the UI.
     */
    private _mapLoyaltyData(loyaltyInfo: LoyaltyInfoRaw): DataLoyalty {
        const adoraConfig = loyaltyInfo.AdoraConfig;

        return {
            providerId: loyaltyInfo.ProviderId ?? LoyaltyProvider.NONE,
            adora: adoraConfig
                ? {
                    programTypeIds: adoraConfig.ProgramTypeIds,
                    unitsName: adoraConfig.UnitsName
                }
                : null,
            punchh: null
        };
    }

    private readonly _mapItemOrderTypeSize = (sz: any): IItemOrderTypeSize => ({
        IsDefault: sz.IDEF,
        Index: sz.IDX,
        IsHalfable: sz.IHLF,
        MinimumCalories: sz.MICALS,
        IsOfferedQR: sz.QR,
        ImageURL: sz.IMGURL,
        SizeId: sz.SZID
    });
}